'use client';

import { ChevronLeft, ChevronRight } from 'lucide-react';
import Link from 'next/link';
import { useTranslations } from 'next-intl';
import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Heading } from '../Heading';
import { IconButton } from '../IconButton';
import styles from './OverflowGrid.module.scss';

type OverflowGridAllowedChildren = React.ReactElement<
  React.ComponentProps<typeof OverflowGridItem>
>;

interface OverflowGridProps {
  id: string;
  children?:
    | OverflowGridAllowedChildren
    | OverflowGridAllowedChildren[]
    | (OverflowGridAllowedChildren | OverflowGridAllowedChildren[])[];
  columns?: number;
  heading: string;
  headingLevel?: 1 | 2 | 3 | 4 | 5 | 6;
  link?: {
    href: string;
    title: string;
    ariaLabel?: string;
  };
}

export const OverflowGrid: React.FC<OverflowGridProps> = ({
  id,
  children,
  columns = 1,
  heading,
  headingLevel = 2,
  link,
}) => {
  const t = useTranslations('components.overflowGrid');
  const gridRef = useRef<HTMLUListElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollButtons = useCallback(() => {
    if (!gridRef.current) return;

    const { scrollLeft, scrollWidth, clientWidth } = gridRef.current;
    setCanScrollLeft(scrollLeft > 0);
    setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
  }, []);

  const scrollTo = useCallback((direction: 'left' | 'right') => {
    if (!gridRef.current) return;

    const scrollAmount = gridRef.current.clientWidth * 0.8;
    const newScrollLeft =
      direction === 'left'
        ? gridRef.current.scrollLeft - scrollAmount
        : gridRef.current.scrollLeft + scrollAmount;

    gridRef.current.scrollTo({
      left: newScrollLeft,
      behavior: 'smooth',
    });
  }, []);

  useEffect(() => {
    const gridElement = gridRef.current;
    if (!gridElement) return;

    // Initial check
    updateScrollButtons();

    // Add scroll listener
    gridElement.addEventListener('scroll', updateScrollButtons, {
      passive: true,
    });

    // Add resize listener to handle responsive changes
    const resizeObserver = new ResizeObserver(updateScrollButtons);
    resizeObserver.observe(gridElement);

    return () => {
      gridElement.removeEventListener('scroll', updateScrollButtons);
      resizeObserver.disconnect();
    };
  }, [updateScrollButtons]);

  const headingId = `${id}-heading`;

  return (
    <section className={styles.section} aria-labelledby={headingId}>
      <header className={styles.header}>
        {link ? (
          <Link
            href={link.href}
            title={link.title}
            aria-label={link.ariaLabel || link.title}
            className={styles.headingLink}
          >
            <Heading level={headingLevel} size="xl" id={headingId} className={styles.headingText}>
              {heading}
              <span className={styles.headingIcon} aria-hidden="true">
                <ChevronRight />
              </span>
            </Heading>
          </Link>
        ) : (
          <Heading level={headingLevel} size="xl" id={headingId}>
            {heading}
          </Heading>
        )}
        {(canScrollLeft || canScrollRight) && (
          <div className={styles.navButtons}>
            <IconButton
              size="sm"
              variant="secondary"
              onClick={() => scrollTo('left')}
              aria-label={t('scrollLeft')}
              label={t('scrollLeft')}
              className={styles.navButtonAction}
              disabled={!canScrollLeft}
            >
              <ChevronLeft aria-hidden="true" />
            </IconButton>
            <IconButton
              size="sm"
              variant="secondary"
              onClick={() => scrollTo('right')}
              aria-label={t('scrollRight')}
              label={t('scrollRight')}
              className={styles.navButtonAction}
              disabled={!canScrollRight}
            >
              <ChevronRight aria-hidden="true" />
            </IconButton>
          </div>
        )}
      </header>
      <div className={styles.gridWrapper}>
        <ul
          ref={gridRef}
          className={styles.grid}
          style={{ '--grid-columns': columns } as React.CSSProperties}
        >
          {children}
        </ul>
      </div>
    </section>
  );
};

interface OverflowGridItemProps {
  children?: React.ReactNode;
}

export const OverflowGridItem: React.FC<OverflowGridItemProps> = ({ children }) => (
  <li className={styles.item}>{children}</li>
);
